import React, {useEffect, useMemo, useState} from 'react';
import {AppBox, AppButton, AppIconButton, AppSelect} from '../index';
import IconChevronLeft from '../icons/chevron-left';
import IconChevronRight from '../icons/chevron-right';
import {ISelectOptions} from './Select';

interface PaginatorProps {
    perPage: number;
    pageCount: number;
    currentPage: number;
    pageChange: (page: number) => void;
    perPageLimitChange: (limit: number) => void;
}

const pageOptions: ISelectOptions[] = [
    {title: '50', value: 50},
    {title: '100', value: 100},
    {title: '150', value: 150},
    {title: '200', value: 200},
];
const getInitialPageWindowSize = () => (window.innerWidth < 800 ? 2 : 4);

const Paginator: React.FC<PaginatorProps> = ({
                                                 perPage,
                                                 pageCount,
                                                 currentPage,
                                                 pageChange,
                                                 perPageLimitChange
                                             }) => {
    const [pageWindowSize, setPageWindowSize] = useState(getInitialPageWindowSize());

    // Set responsive page window size
    useEffect(() => {
        const handleResize = () => setPageWindowSize(getInitialPageWindowSize());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const getPageRange = (): number[] => {
        const halfWindow = Math.floor(pageWindowSize / 2);
        let start = Math.max(1, currentPage - halfWindow);
        let end = Math.min(pageCount, start + pageWindowSize - 1);
        // adjust start again if end hit max
        start = Math.max(1, end - pageWindowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const pages = useMemo(getPageRange, [currentPage, pageCount, pageWindowSize]);

    const onSelectPage = (page: number) => {
        if (page >= 1 && page <= pageCount && page !== currentPage) {
            pageChange(page);
        }
    };

    const onPageLimitChange = (option: ISelectOptions) => {
        perPageLimitChange(option.value as number);
    };

    const selectedOption = pageOptions.find((option) => option.value === perPage);

    return (
        <section aria-label="pagination" className="pagination">
            <AppBox alignItems="center" gap="xs">
                <label htmlFor="perPageLimit">Items per page</label>
                <AppSelect
                    currentOption={selectedOption}
                    onChange={onPageLimitChange}
                    options={pageOptions}
                />
            </AppBox>

            {pageCount > 1 && (
                <AppBox gap="3xs">
                    <AppIconButton
                        variant="outline"
                        size="sm"
                        icon={IconChevronLeft()}
                        disabled={currentPage === 1}
                        onClick={() => onSelectPage(currentPage - 1)}
                    />

                    <AppBox gap="3xs">
                        {/* Show first page if not in range */}
                        {pages[0] > 1 && (
                            <>
                                <AppButton
                                    label="1"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onSelectPage(1)}
                                />
                                {pages[0] > 2 && <AppBox justifyContent="center" className="w-4 h-4">...</AppBox>}
                            </>
                        )}

                        {/* Dynamic range of pages */}
                        {pages.map((page) => (
                            <AppButton
                                key={page}
                                label={page.toString()}
                                size="sm"
                                variant={page === currentPage ? 'filled' : 'outline'}
                                onClick={() => onSelectPage(page)}
                            />
                        ))}

                        {/* Show last page if not in range */}
                        {pages[pages.length - 1] < pageCount && (
                            <>
                                {pages[pages.length - 1] + 1 < pageCount && (
                                    <AppBox justifyContent="center" className="w-4 h-4">...</AppBox>
                                )}
                                <AppButton
                                    label={pageCount.toString()}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onSelectPage(pageCount)}
                                />
                            </>
                        )}
                    </AppBox>

                    <AppIconButton
                        variant="outline"
                        size="sm"
                        icon={IconChevronRight()}
                        disabled={currentPage === pageCount}
                        onClick={() => onSelectPage(currentPage + 1)}
                    />
                </AppBox>
            )}
        </section>
    );
};

export default Paginator;